import { useCallback, useEffect, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Loader2, Mail, Save, Trash2, Check, RotateCcw, Wand2, FileText,
  ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi, opsOpen, money, pct } from "../../lib/api";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import PortalSheet from "./PortalSheet";
import PackagePanel from "./PackagePanel";

const TONE = { ok: "text-slate-600", good: "text-emerald-700", bad: "text-rose-700",
  none: "text-slate-400" };

/** One application for payment: the G703 continuation sheet on top, the G702 summary below.
 *  Columns are the standard ones — D scheduled, E previous, F this period, G stored,
 *  H completed and stored, I % complete, K balance, L retainage. */
export default function PayAppDetail({ project, appId, onBack, onChanged }) {
  const [app, setApp] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [paid, setPaid] = useState({ amount: "", date: "" });
  const [paying, setPaying] = useState(false);
  const [portal, setPortal] = useState(false);

  const load = useCallback(async () => {
    try { setApp(await opsApi.payApp(project.id, appId)); setEdits({}); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load that application"); }
  }, [project.id, appId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(edits).map(([sov_line_id, v]) => ({
        sov_line_id,
        work_this_period: Number(v.work_this_period ?? 0),
        stored_materials: Number(v.stored_materials ?? 0),
      }));
      setApp(await opsApi.updatePayApp(project.id, appId, { lines }));
      setEdits({});
      toast.success("Application updated");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be saved"); }
    finally { setBusy(false); }
  };

  const move = async (status, extra = {}) => {
    setBusy(true);
    try {
      setApp(await opsApi.payAppStatus(project.id, appId, { status, ...extra }));
      toast.success(`${app.number} is ${status}`);
      setPaying(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That move was refused"); }
    finally { setBusy(false); }
  };

  const email = async () => {
    try { setDraft(await opsApi.emailDraft(project.id, "pay_app", appId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build that email"); }
  };

  const discard = async () => {
    if (!window.confirm(`Discard ${app.number}? Nothing has been billed on it.`)) return;
    try {
      await opsApi.discardPayApp(project.id, appId);
      toast.success("Discarded");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not discard it"); }
  };

  if (!app) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading application…
    </div>;
  }
  const t = app.totals;
  const dirty = Object.keys(edits).length > 0;
  const val = (l, field) => (edits[l.sov_line_id]?.[field] ?? l[field]);
  const set = (l, field, v) => setEdits((p) => ({
    ...p,
    [l.sov_line_id]: {
      work_this_period: l.work_this_period, stored_materials: l.stored_materials,
      ...p[l.sov_line_id], [field]: v,
    },
  }));

  return (
    <div className="space-y-4" data-testid="ops-payapp-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Breadcrumbs trail={[{ label: "Applications", dropParam: "payapp", testId: "ops-payapp-back" }]} />
        <span className="text-base font-semibold text-slate-900" data-testid="ops-payapp-number">
          {app.number}{app.revision > 1 ? ` R${app.revision}` : ""}
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
              data-testid="ops-payapp-status">{app.status_label}</span>
        <span className="text-xs text-slate-500">{app.kind_label}</span>
        <span className="text-xs text-slate-500">
          period ending {app.period_end || "—"} · retainage {pct(app.retainage_pct)}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="ops-payapp-pdf"
                  onClick={() => opsOpen(
                    `/projects/${project.id}/invoicing/pay-applications/${appId}/pdf`)
                    .catch(() => toast.error("That PDF could not be rendered"))}>
            <FileText className="mr-1.5 h-3.5 w-3.5" /> G702 / G703 PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPortal(true)}
                  data-testid="ops-payapp-portal">
            <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> GC portal sheet
          </Button>
          {app.editable && dirty && (
            <Button size="sm" onClick={save} disabled={busy} data-testid="ops-payapp-save">
              <Save className="mr-1.5 h-3.5 w-3.5" /> Save
            </Button>
          )}
          {app.editable && (
            <Button variant="outline" size="sm" onClick={email} disabled={busy || dirty}
                    title={dirty ? "Save your changes first" : "Emailing it submits it"}
                    data-testid="ops-payapp-email">
              <Mail className="mr-1.5 h-3.5 w-3.5" /> Email it to the GC
            </Button>
          )}
          {app.status === "draft" && (
            <Button variant="outline" size="sm" onClick={() => move("pm_approved")} disabled={busy || dirty}
                    data-testid="ops-payapp-pm-approve">PM approve</Button>
          )}
          {app.status === "pm_approved" && (
            <Button variant="outline" size="sm" disabled={busy}
                    onClick={() => move("accountant_approved")}
                    data-testid="ops-payapp-acct-approve">Accounting approve</Button>
          )}
          {["draft", "accountant_approved"].includes(app.status) && (
            <Button variant="outline" size="sm" onClick={() => move("submitted")} disabled={busy}
                    data-testid="ops-payapp-submit">Mark submitted</Button>
          )}
          {app.status === "submitted" && (
            <Button variant="outline" size="sm" onClick={() => move("approved")} disabled={busy}
                    data-testid="ops-payapp-approve">
              <Check className="mr-1.5 h-3.5 w-3.5" /> Approved by the GC
            </Button>
          )}
          {["submitted", "approved"].includes(app.status) && (
            <Button size="sm" onClick={() => { setPaid({ amount: t.payment_due, date: "" });
              setPaying(true); }} disabled={busy} data-testid="ops-payapp-paid">
              Record payment
            </Button>
          )}
          {["submitted", "approved"].includes(app.status) && (
            <Button variant="ghost" size="sm" onClick={() => move("rejected")} disabled={busy}
                    data-testid="ops-payapp-reject" className="text-slate-500 hover:text-rose-700">
              Rejected
            </Button>
          )}
          {app.status === "rejected" && (
            <Button size="sm" disabled={busy} data-testid="ops-payapp-revise"
                    onClick={async () => {
                      const next = await opsApi.revisePayApp(project.id, appId);
                      toast.success(`${next.number} R${next.revision} drafted`);
                      onChanged?.();
                      onBack();
                    }}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Revise it
            </Button>
          )}
          {app.status === "draft" && (
            <Button variant="ghost" size="sm" onClick={discard} disabled={busy}
                    data-testid="ops-payapp-discard"
                    className="text-slate-500 hover:text-rose-700">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {!app.ties_to_contract && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
           data-testid="ops-payapp-out-of-balance">{app.sov_check}</p>
      )}
      {app.over_under?.state && app.over_under.state !== "none" && (
        <p className={`rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs ${
          TONE[app.over_under.tone] || ""}`} data-testid="ops-payapp-over-under">
          {app.over_under.line}: billed {pct(app.over_under.billed_pct)} of the revised contract
          {" "}against {pct(app.over_under.spent_pct)} of the revised budget spent.
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="max-h-[55vh] overflow-auto">
          <table className="w-full text-xs" data-testid="ops-payapp-lines">
            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-left">Description Of Work</th>
                <th className="px-3 py-2 text-right">Scheduled Value</th>
                <th className="px-3 py-2 text-right">Previous</th>
                <th className="px-3 py-2 text-right">This Period</th>
                <th className="px-3 py-2 text-right">Stored Materials</th>
                <th className="px-3 py-2 text-right">Completed &amp; Stored</th>
                <th className="px-3 py-2 text-right">%</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-right">Retainage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {app.lines.map((l) => (
                <tr key={l.id} data-testid={`ops-payapp-line-${l.cost_code}`}
                    className={l.kind === "co" ? "bg-violet-50/40" : ""}>
                  <td className="px-3 py-1.5 font-mono">{l.cost_code}</td>
                  <td className="px-3 py-1.5">
                    {l.description}
                    {l.suggestion && l.suggestion !== "no movement suggested" && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-blue-700"
                            title={l.suggestion} data-testid={`ops-payapp-why-${l.cost_code}`}>
                        <Wand2 className="h-2.5 w-2.5" />{l.suggested_from}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {money(l.scheduled_value)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                    {money(l.previous_billed)}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {app.editable ? (
                      <Input type="number" step="0.01" value={val(l, "work_this_period")}
                             onChange={(e) => set(l, "work_this_period", e.target.value)}
                             className="ml-auto h-7 w-28 text-right text-xs"
                             data-testid={`ops-payapp-work-${l.cost_code}`} />
                    ) : <span className="tabular-nums">{money(l.work_this_period)}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {app.editable ? (
                      <Input type="number" step="0.01" value={val(l, "stored_materials")}
                             onChange={(e) => set(l, "stored_materials", e.target.value)}
                             className="ml-auto h-7 w-28 text-right text-xs"
                             data-testid={`ops-payapp-stored-${l.cost_code}`} />
                    ) : <span className="tabular-nums">{money(l.stored_materials)}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                    {money(l.total_completed_and_stored)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{pct(l.pct_complete)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {money(l.balance_to_finish)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(l.retainage)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-slate-50 text-xs font-semibold">
              <tr>
                <td colSpan={2} className="px-3 py-2">Totals</td>
                <td className="px-3 py-2 text-right tabular-nums"
                    data-testid="ops-payapp-scheduled-total">{money(app.scheduled_total)}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums"
                    data-testid="ops-payapp-this-period">{money(t.this_period)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(t.stored_materials)}</td>
                <td className="px-3 py-2 text-right tabular-nums"
                    data-testid="ops-payapp-completed">{money(t.completed_and_stored)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{pct(t.pct_complete)}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right tabular-nums">{money(t.retainage_held)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <section className="max-w-xl rounded-xl border border-slate-200 bg-white p-4"
               data-testid="ops-payapp-summary">
        <h3 className="text-sm font-semibold text-slate-900">Application Summary</h3>
        <dl className="mt-2 space-y-1 text-xs">
          {[["Original contract", t.original_contract], ["Net change orders", t.net_cos],
            ["Revised contract", t.revised_contract],
            ["Total completed and stored", t.completed_and_stored],
            [`Retainage held (${pct(app.retainage_pct)})`, t.retainage_held],
            ...(t.retainage_released ? [["Retainage released", t.retainage_released]] : []),
            ["Total earned less retainage", t.earned_less_retainage],
            ["Less previous payments", t.previous_payments]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4">
              <dt className="text-slate-500">{k}</dt>
              <dd className="font-medium tabular-nums text-slate-900">{money(v)}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 border-t border-slate-200 pt-1.5">
            <dt className="font-semibold text-slate-900">PAYMENT DUE</dt>
            <dd className="text-base font-semibold tabular-nums text-slate-900"
                data-testid="ops-payapp-payment-due">{money(t.payment_due)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Balance to finish, plus retainage</dt>
            <dd className="font-medium tabular-nums text-slate-900">
              {money(t.balance_to_finish_plus_retainage)}
            </dd>
          </div>
        </dl>
      </section>

      <PackagePanel project={project} appId={appId} onChanged={() => { load(); onChanged?.(); }} />

      {paying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
             onClick={() => setPaying(false)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
               onClick={(e) => e.stopPropagation()} data-testid="ops-payapp-paid-dialog">
            <h3 className="text-base font-semibold text-slate-900">
              Record the payment on {app.number}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Marking it paid raises the lien waiver for this payment.
            </p>
            <label className="mt-3 block text-xs">
              <span className="text-slate-500">Amount received</span>
              <Input type="number" step="0.01" value={paid.amount}
                     onChange={(e) => setPaid((p) => ({ ...p, amount: e.target.value }))}
                     className="mt-1 h-9 text-sm" data-testid="ops-payapp-paid-amount" />
            </label>
            <label className="mt-2 block text-xs">
              <span className="text-slate-500">Date received</span>
              <Input type="date" value={paid.date}
                     onChange={(e) => setPaid((p) => ({ ...p, date: e.target.value }))}
                     className="mt-1 h-9 text-sm" data-testid="ops-payapp-paid-date" />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPaying(false)}
                      data-testid="ops-payapp-paid-cancel">Cancel</Button>
              <Button size="sm" disabled={busy} data-testid="ops-payapp-paid-confirm"
                      onClick={() => move("paid", { paid_amount: Number(paid.amount),
                        paid_date: paid.date })}>
                Record it
              </Button>
            </div>
          </div>
        </div>
      )}

      {portal && (
        <PortalSheet project={project} appId={appId} onClose={() => setPortal(false)} />
      )}

      {draft && (
        <FrameworkEmailDialog projectId={project.id} draft={draft} onClose={() => setDraft(null)}
                              onSent={() => { setDraft(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}
